import React, { useEffect, useMemo, useState } from "react";
import { Box, Tabs, Tab, Typography } from "@mui/material";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import SecurityTab from "./SecurityTab";
import ProfileTab from "./ProfileTab";
import BillingTab from "./BillingTab";
import CommunicationPreferencesTab from "./NotificationsTab";
import OrganizationsTab from "./OrganizationsTab";
import "./AccountSettings.css";

const resolveSelectedTabFromSearch = (search, allowedTabKeys) => {
  const fallbackTab = allowedTabKeys.has("organizations")
    ? "organizations"
    : "security";
  const params = new URLSearchParams(search || "");
  if (
    params.has("orgBillingSetup") ||
    params.has("orgBillingCanceled") ||
    params.has("orgCreditPurchase") ||
    params.has("orgCreditPurchaseCanceled")
  ) {
    return "organizations";
  }

  if (params.has("added") || params.has("canceled")) {
    return allowedTabKeys.has("workspace") ? "workspace" : fallbackTab;
  }

  const tabKey = String(params.get("tab") || "").trim().toLowerCase();
  const normalizedTabKey = tabKey === "billing" ? "workspace" : tabKey;

  return allowedTabKeys.has(normalizedTabKey) ? normalizedTabKey : fallbackTab;
};

const AccountSettings = () => {
  const location = useLocation();
  const selectedWorkspaceKey = useSelector(
    (state) => state.workspace.selectedWorkspaceKey,
  );
  const isOrganizationWorkspace =
    typeof selectedWorkspaceKey === "string" &&
    selectedWorkspaceKey.startsWith("org:");
  const visibleTabs = useMemo(
    () => [
      { key: "profile", label: "Profile" },
      { key: "security", label: "Security" },
      ...(!isOrganizationWorkspace
        ? [{ key: "workspace", label: "Personal Workspace" }]
        : []),
      { key: "communications", label: "Communications" },
      { key: "organizations", label: "Organizations" },
    ],
    [isOrganizationWorkspace],
  );
  const allowedTabKeys = useMemo(
    () => new Set(visibleTabs.map((tab) => tab.key)),
    [visibleTabs],
  );
  const [selectedTab, setSelectedTab] = useState(() =>
    resolveSelectedTabFromSearch(location.search, allowedTabKeys),
  );

  useEffect(() => {
    setSelectedTab(resolveSelectedTabFromSearch(location.search, allowedTabKeys));
  }, [location.search, allowedTabKeys]);

  const renderTabContent = () => {
    switch (selectedTab) {
      case "profile":
        return <ProfileTab />;
      case "security":
        return <SecurityTab />;
      case "workspace":
        return <BillingTab />;
      case "communications":
        return <CommunicationPreferencesTab />;
      case "organizations":
        return <OrganizationsTab />;
      default:
        return null;
    }
  };

  return (
    <Box className="account-settings-wrapper">
      <Typography variant="h4" mt={2} mb={2} ml={2}>
        Account Settings
      </Typography>

      <Tabs
        value={selectedTab}
        onChange={(e, newValue) => setSelectedTab(newValue)}
        className="settings-tabs"
        variant="scrollable"
        scrollButtons="auto"
        TabIndicatorProps={{
          style: {
            backgroundColor: "#00e676",
            height: "3px",
            borderRadius: "2px",
          },
        }}
      >
        {visibleTabs.map((tab) => (
          <Tab label={tab.label} value={tab.key} key={tab.key} />
        ))}
      </Tabs>

      <Box className="tab-content">{renderTabContent()}</Box>
    </Box>
  );
};

export default AccountSettings;
